import { useMemo, useState } from 'react';
import { IFamilyMemberChildDto } from '../../../../utils/constants/interfaces';
import { SortDirection } from '../../../../utils/constants/enums';

interface UseFamilyMemberChildrenProps {
  familyMemberChildren: IFamilyMemberChildDto[];
  loadingChildren: boolean;
}

export const useFamilyMemberChildren = ({
  familyMemberChildren,
  loadingChildren
}: UseFamilyMemberChildrenProps) => {
  const [sortField, setSortField] = useState<string>('firstName');
  const [sortDirection, setSortDirection] = useState<SortDirection>(SortDirection.ASCENDING);

  const hasChildren = useMemo(() => {
    return familyMemberChildren.length > 0;
  }, [familyMemberChildren.length]);

  const childrenCount = useMemo(() => {
    return familyMemberChildren.length;
  }, [familyMemberChildren.length]);

  const isLoading = useMemo(() => {
    return loadingChildren;
  }, [loadingChildren]);

  const sortedChildren = useMemo(() => {
    if (!familyMemberChildren || familyMemberChildren.length === 0) {
      return [];
    }

    return [...familyMemberChildren].sort((a, b) => {
      let aValue: any = a[sortField as keyof IFamilyMemberChildDto];
      let bValue: any = b[sortField as keyof IFamilyMemberChildDto];

      // Handle date fields
      if (sortField.includes('Date') || sortField.includes('date')) {
        aValue = aValue ? new Date(aValue) : new Date(0);
        bValue = bValue ? new Date(bValue) : new Date(0);
      }

      // Handle string comparisons
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
      }

      // Handle null values
      if (aValue === null && bValue === null) return 0;
      if (aValue === null) return sortDirection === SortDirection.ASCENDING ? -1 : 1;
      if (bValue === null) return sortDirection === SortDirection.ASCENDING ? 1 : -1;

      if (aValue < bValue) {
        return sortDirection === SortDirection.ASCENDING ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortDirection === SortDirection.ASCENDING ? 1 : -1;
      }
      return 0;
    });
  }, [familyMemberChildren, sortField, sortDirection]);

  const changeSort = (newSortField: string) => {
    if (sortField === newSortField) {
      // Same field clicked - toggle direction
      setSortDirection(
        sortDirection === SortDirection.ASCENDING
          ? SortDirection.DESCENDING
          : SortDirection.ASCENDING
      );
    } else {
      // Different field clicked - start with ascending
      setSortField(newSortField);
      setSortDirection(SortDirection.ASCENDING);
    }
  };

  return {
    hasChildren,
    childrenCount,
    isLoading,
    sortedChildren,
    sortField,
    sortDirection,
    changeSort
  };
};
